import { defineStore } from 'pinia'
import { getLeverageInfo, getMarginType, getOpenOrders, getPositions, getStopOrders, getTradingAccount } from '@/apis/swap'
import { formatter } from '@/apis/swapClientWs'
import { useSwapStore } from './swap'
import { useSwapMarketStore } from './swapMarket'

export const useSwapClientStore = defineStore('swapClient', {
  state: () => ({
    indexAccount: {},
    tradingAccount: {},
    leverageInfo: {},
    marginTypeRaw: {},
    positions: [],
    openOrders: [],
    stopOrders: [],
  }),
  getters: {
    ordersN() {
      return +add(this.openOrdersN, this.stopOrdersN)
    },
    stopOrdersN() {
      return this.stopOrdersRaw._ALL.length
    },
    openOrdersN() {
      return this.openOrdersRaw._ALL.length
    },
    positionsN() {
      return this.positionsRaw._ALL.length
    },
    getLeverage() {
      const swap = useSwapStore()
      return (symbol = swap.symbol, marginType) => {
        marginType ??= this.marginTypeRaw[symbol]
        return this.leverageInfo[swap.tradeType]?.[symbol]?.[marginType]?.leverage || 1
      }
    },
    positionConfig() {
      return this.getPositionConfig()
    },
    getPositionConfig(s) {
      const swap = useSwapStore()
      return (symbol = swap.symbol, marginType) => {
        marginType ??= s.marginTypeRaw[symbol]
        const leverage = this.getLeverage(symbol, marginType)
        const { maxLeverage } = swap.getInfo(symbol)
        const riskLimit = swap.getRiskLimit({ symbol, marginType, leverage }) || {}
        return {
          riskLimit,
          riskLimits: (swap.riskLimitsRaw[symbol] || []).filter(i => i.marginType === marginType),
          maxLeverage,
          marginType,
          isolated: marginType === 'isolated',
          maxValueLimit: riskLimit.notionalCap,
          leverage,
        }
      }
    },
    getBalance: (s) => {
      const swap = useSwapStore()
      return (currency = swap.baseM) => {
        const tradingAccount = s.tradingAccount[currency] || {}
        const indexAccount = s.indexAccount[currency] || {}
        return {
          ...tradingAccount,
          ...indexAccount,
        }
      }
    },
    getOrderLots(s) {
      const swap = useSwapStore()
      return (symbol = swap.symbol) => {
        const lots = { '1': 0, '-1': 0 }
        this.openOrdersRaw._ALL.filter(i => i.symbol === symbol).forEach((i) => {
          if (i.positionEffect === 1) {
            const side = i.positionSide === 'long' ? 1 : -1
            lots[side] += Math.abs(i.leavesQty)
          }
        })
        lots.max = Math.max(lots[1], lots['-1'], 0)
        lots.min = Math.min(lots[1], lots['-1'])
        lots.total = lots[1] - lots['-1']
        return lots
      }
    },
    getPositionLots(s) {
      const swap = useSwapStore()
      return (symbol = swap.symbol) => {
        const lots = { '1': 0, '-1': 0 }
        this.positionsRaw._ALL.filter(i => i.symbol === symbol).forEach((i) => {
          const side = i.positionSide === 'long' ? 1 : -1
          lots[side] += Math.abs(i.positionAmt)
        })
        lots.max = Math.max(lots[1], lots['-1'], 0)
        lots.min = Math.min(lots[1], lots['-1'])
        lots.total = lots[1] - lots['-1']
        return lots
      }
    },
    positionsRaw(s) {
      const swap = useSwapStore()
      const swapMarket = useSwapMarketStore()
      return s.positions.reduce((acc, next) => {
        const { symbol, marginType, positionAmt, positionSide, posMargin, avgEntryPrice, positionId, leverage } = next
        const { lp } = swapMarket.getOverview(symbol)
        const { size = 0 } = swap.getInfo(symbol)
        const initMargin = mul(positionAmt, size).mul(avgEntryPrice).div(leverage)
        const side = positionSide === 'long' ? 1 : -1
        const upnl = lp ? sub(lp, avgEntryPrice).mul(positionAmt).mul(size).mul(side) : '--'
        const roe = posMargin ? div(upnl || 0, posMargin) : 0
        acc[positionId] = {
          ...next,
          id: positionId,
          isolated: marginType === 'isolated',
          initMargin,
          upnl,
          roe,
        }
        acc._ALL.push(acc[positionId])
        acc.tupnl = add(acc.tupnl, upnl)
        return acc
      }, { _ALL: [], tupnl: 0 })
    },
    openOrdersRaw(s) {
      return s.openOrders.reduce((acc, next) => {
        const { marginType, orderId } = next
        acc[orderId] = {
          ...next,
          id: orderId,
          positionEffect: ['buylong', 'sellshort'].includes(`${next.side}${next.positionSide}`) ? 1 : 2,
          isolated: marginType === 'isolated',
          lm: next.orderType === 'limit',
        }
        acc[next.symbol] ??= []
        acc[next.symbol].push(acc[orderId])
        acc._ALL.push(acc[orderId])
        return acc
      }, { _ALL: [] })
    },
    stopOrdersRaw(s) {
      return s.stopOrders.reduce((acc, next) => {
        const { marginType, orderId } = next
        acc[orderId] = {
          ...next,
          id: orderId,
          isolated: marginType === 'isolated',
        }
        acc._ALL.push(acc[orderId])
        return acc
      }, { _ALL: [] })
    },
    getRiskFeeInfo() {
      const swap = useSwapStore()
      return (o) => {
        const { takerRate } = swap.getInfo(o.symbol)
        const positionMargin = swap.getRealAmount(o.positionAmt, { symbol: o.symbol, forceQuote: true, price: o.avgEntryPrice }) // 仓位价值
        const { maintMarginRatio } = swap.getRiskLimit({ ...o, positionMargin }) || {}
        const mmr = mul(maintMarginRatio, positionMargin) // 维持保证金
        const fundingFee = mul(Math.min(0, o.fundingRate), positionMargin) // 资金费用取小
        const fundingFeeOrigin = mul(o.fundingRate, positionMargin) // 初始资金费用
        const fee = mul(takerRate, positionMargin) // 手续费
        return {
          mmr,
          fundingFee,
          fundingFeeOrigin,
          fee,
          maintMarginRatio,
          fundingRate: o.fundingRate,
          takerRate,
          totalFee: add(fundingFee, fee).add(mmr),
        }
      }
    },
    // 逐仓风险率=（逐仓维持保证金+abs（min(0,本期资金费)）+手续费）/ 逐仓权益
    // 全仓风险率 = (sum全仓各交易对维持保证金+abs（min(0,本期资金费)）+手续费） / 全仓权益
    getRiskRateById() {
      return (id) => {
        const o = this.positionsRaw[id]
        if (o) {
          if (o.isolated) {
            const { totalFee } = this.getRiskFeeInfo(o)
            return div(totalFee, add(o.posMargin, o.upnl))
          }
          else {
            const positions = this.positionsRaw._ALL.filter(i => !i.isolated)
            const totalFee = positions.reduce((acc, next) => {
              const { totalFee } = this.getRiskFeeInfo(next)
              return add(acc, totalFee)
            }, 0)
            // 全仓权益 = 账户权益 - 逐仓权益
            const isolatedBalance = this.positionsRaw._ALL.filter(i => i.isolated).reduce((acc, next) => add(acc, next.posMargin).add(next.upnl), 0)
            const balance = sub(this.getBalance().total, isolatedBalance)
            return div(totalFee, balance)
          }
        }
      }
    },
    // p 是仓位方向 （多仓 P = 1、空仓 P = -1）
    // 全仓强平价格公式: A强平价格 =【全仓权益 - sum其他交易对（维系保证金率 + Max(p * 资金费率, 0)x数量 + 强平手续费）-P * A标记价格 * A净持仓数量】/【A数量*（A维系保证金率+max(P*预付资金费率,0)+taker手续费率）+B数量*（B维系保证金率+max(p*预付资金费率,0)-P*A净持仓数量】
    // 逐仓强平价格公式： 强平价格 =（持仓均价 -（仓位保证金）/持仓数量*p）/（1 - p *（维持保证金 + max（p * 资金费率，0）+taker手续费率））
    getLiquidPrice() {
      const swap = useSwapStore()
      return (o) => {
        const { symbol, avgEntryPrice, posMargin, positionAmt, positionSide } = o || {}
        const { size } = swap.getInfo(symbol)
        const p = positionSide === 'long' ? 1 : -1
        const { maintMarginRatio, fundingRate, takerRate } = this.getRiskFeeInfo(o)
        const fz = sub(avgEntryPrice, div(posMargin, mul(positionAmt, size).mul(p)))
        const fm = sub(1, mul(p, add(maintMarginRatio, Math.max(0, mul(fundingRate, p))).add(takerRate)))
        console.table([
          avgEntryPrice,
          +posMargin,
          positionAmt,
          p,
          maintMarginRatio,
          fundingRate,
          takerRate,
        ])
        return fz.div(fm)
      }
    },
  },
  actions: {
    async getPositions() {
      const swap = useSwapStore()
      const { success, data } = await getPositions({ tradeType: swap.tradeType })
      if (success) {
        this.positions = data
      }
    },
    async getOpenOrders() {
      const swap = useSwapStore()
      const { success, data } = await getOpenOrders({ tradeType: swap.tradeType })
      if (success) {
        this.openOrders = data
      }
    },
    async getStopOrders() {
      const swap = useSwapStore()
      const { success, data } = await getStopOrders({ tradeType: swap.tradeType })
      if (success) {
        this.stopOrders = data
      }
    },
    async getTradingAccount({ currency = 'USDT' } = {}) {
      const { success, data } = await getTradingAccount({ currency })
      // rest数据全，ws分开的，混着瞎弄吧
      if (success) {
        formatter.tradingAccount({ data: { proto: data } })
        formatter.indexAccount({ data: { proto: data } })
      }
    },
    async getMarginType({ symbol } = {}) {
      const swap = useSwapStore()
      symbol ??= swap.symbol
      const { success, data } = await getMarginType({ symbol, tradeType: swap.tradeType })
      if (success) {
        this.marginTypeRaw[data.symbol] = data.marginType
      }
    },
    async getLeverageInfo({ symbol } = {}) {
      const swap = useSwapStore()
      symbol ??= swap.symbol
      const { success, data } = await getLeverageInfo({ symbol, tradeType: swap.tradeType })
      if (success) {
        formatter.leverage({ data: { proto: data } })
      }
    },
  },
})
